// frontend/packages/business-console/src/components/build-interview/BriefPanel.jsx
//
// Right column of the build interview (Website Builder Redesign, Phase 1): the
// self-building "Your brief" panel. A completion ring header, then one block per
// brief section with a coloured left accent bar and a done / active / upcoming
// state. Done sections carry an inline Edit link that jumps back to that step.
// Out-of-envelope asks (customFlags) render as "For our team" chips.
//
// Driven by the server step list + brief data. Colors via useTokens.

import React from 'react';
import { Box, Typography, Chip, Stack, Divider } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import useTokens from '../primitives/useTokens';
import CompletionRing from './CompletionRing';
import { SECTION_LABELS, summarizeSection } from './briefSummary';

function SectionBlock({ step, value, state, tokens, canEdit, onEdit }) {
  const t = tokens;
  const accent =
    state === 'done' ? t.green : state === 'active' ? t.blue : t.grayBorder;
  const titleColor =
    state === 'done' ? t.greenText : state === 'active' ? t.blue : t.faint;
  return (
    <Box
      sx={{
        pl: 1.25,
        py: 0.75,
        borderLeft: `3px solid ${accent}`,
        opacity: state === 'upcoming' ? 0.55 : 1,
        background:
          state === 'active' ? 'rgba(37,99,235,.05)' : 'transparent',
        borderRadius: '0 6px 6px 0',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {state === 'done' ? (
          <CheckIcon sx={{ fontSize: 13, color: t.green }} />
        ) : null}
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '.03em',
            textTransform: 'uppercase',
            color: titleColor,
            flex: 1,
            minWidth: 0,
          }}
        >
          {step.title}
        </Typography>
        {state === 'done' && canEdit ? (
          <Box
            component="button"
            type="button"
            onClick={() => onEdit && onEdit(step.id)}
            sx={{
              font: 'inherit',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.25,
              fontSize: 10.5,
              fontWeight: 700,
              color: t.blue,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              p: 0,
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: 12 }} /> Edit
          </Box>
        ) : null}
      </Box>
      {state === 'done' ? (
        <Typography
          sx={{
            fontSize: 12.5,
            color: t.textSecondary,
            mt: 0.25,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {summarizeSection(step.id, value)}
        </Typography>
      ) : state === 'active' ? (
        <Typography sx={{ fontSize: 12, fontStyle: 'italic', color: t.blue, mt: 0.25 }}>
          Filling in now
        </Typography>
      ) : null}
    </Box>
  );
}

export default function BriefPanel({
  steps = [],
  data = null,
  activeId = null,
  percent = 0,
  completed = false,
  busy = false,
  onEdit,
  customFlags = [],
}) {
  const t = useTokens();
  const sectionSteps = steps.filter((s) => SECTION_LABELS[s.id]);
  const canEdit = !completed && !busy;

  return (
    <Box
      sx={{
        width: { xs: '100%', md: 300 },
        flexShrink: 0,
        alignSelf: 'flex-start',
        background: t.card,
        border: `1px solid ${t.cardBorder}`,
        borderRadius: '16px',
        boxShadow: t.cardShadowSoft,
        p: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
        <CompletionRing percent={percent} />
        <Box>
          <Typography sx={{ fontSize: 14, fontWeight: 800, color: t.text }}>
            Your brief
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: t.faint }}>
            building as you answer
          </Typography>
        </Box>
      </Box>

      <Stack spacing={0.75}>
        {sectionSteps.map((s) => {
          const stateName = s.done
            ? 'done'
            : s.id === activeId
              ? 'active'
              : 'upcoming';
          return (
            <SectionBlock
              key={s.id}
              step={s}
              value={data ? data[s.id] : undefined}
              state={stateName}
              tokens={t}
              canEdit={canEdit}
              onEdit={onEdit}
            />
          );
        })}
      </Stack>

      {customFlags && customFlags.length > 0 ? (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography sx={{ fontSize: 11, fontWeight: 800, color: t.muted, mb: 0.75 }}>
            For our team
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {customFlags.map((f, i) => (
              <Chip key={i} size="small" label={f.note || f.flag} />
            ))}
          </Stack>
        </>
      ) : null}
    </Box>
  );
}
